import { useLocation, useNavigate } from 'react-router-dom';
import { useLiff } from '@/hooks/useLiff';
import { Button, Card } from '@/components/common';
import { useEffect, useMemo, useState } from 'react';
import { buildFlaroUrl, type InvitationEnv } from '@/types/invitation';
import { isDebugMode } from '@/utils/debugLogger';

interface DebugInfo {
  apiResponse?: Record<string, unknown>;
  debugLogs?: string[];
}

interface CompleteState {
  staffName: string;
  lineDisplayName: string;
  env: InvitationEnv;
  companyCode: string;
  debugInfo?: DebugInfo;
}

export const CompletePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const liff = useLiff();
  const [showDebugPanel, setShowDebugPanel] = useState(false);

  const state = location.state as CompleteState | null;
  const debugMode = useMemo(() => isDebugMode(), []);
  const debugInfo = state?.debugInfo;

  // stateがない場合はトップページにリダイレクト
  useEffect(() => {
    if (!state) {
      navigate('/', { replace: true });
    }
  }, [state, navigate]);

  const handleClose = () => {
    liff.closeLiff();
  };

  // APIレスポンスからトークン情報を取得
  const apiResponse = debugInfo?.apiResponse as Record<string, unknown> | undefined;
  const hasAllTokens = apiResponse &&
    apiResponse.id_token &&
    apiResponse.access_token &&
    apiResponse.refresh_token &&
    apiResponse.email;

  // 実際に開かれるFLARO URLを生成
  const getFlaroUrl = () => {
    if (!state) return '';
    const baseUrl = buildFlaroUrl(state.env, state.companyCode);

    // トークンがある場合は /auth/line-callback にリダイレクト
    if (hasAllTokens) {
      const callbackUrl = new URL(`${baseUrl}/auth/line-callback`);
      const fragment = new URLSearchParams({
        access_token: apiResponse.access_token as string,
        id_token: apiResponse.id_token as string,
        refresh_token: apiResponse.refresh_token as string,
        expires_in: String(apiResponse.expires_in || 3600),
        email: apiResponse.email as string,
      }).toString();
      callbackUrl.hash = fragment;
      return callbackUrl.toString();
    }

    // トークンがない場合は従来通り
    if (debugMode) {
      return `${baseUrl}/login?debug=1`;
    }
    return baseUrl;
  };

  const handleOpenFlaro = () => {
    if (!state) return;
    const flaroUrl = getFlaroUrl();
    liff.openWindow(flaroUrl, true);
  };

  if (!state) {
    return null;
  }

  // デバッグパネルのレンダリング
  const renderDebugPanel = () => {
    if (!debugMode) return null;

    // 診断結果を生成
    const diagnosis: string[] = [];

    if (apiResponse) {
      // トークンの有無をチェック
      const hasIdToken = !!apiResponse.id_token;
      const hasAccessToken = !!apiResponse.access_token;
      const hasRefreshToken = !!apiResponse.refresh_token;
      const hasEmail = !!apiResponse.email;

      diagnosis.push(hasIdToken ? '✅ id_token: あり' : '❌ id_token: なし');
      diagnosis.push(hasAccessToken ? '✅ access_token: あり' : '❌ access_token: なし');
      diagnosis.push(hasRefreshToken ? '✅ refresh_token: あり' : '❌ refresh_token: なし');
      diagnosis.push(hasEmail ? `✅ email: ${apiResponse.email}` : '❌ email: なし');

      if (!hasIdToken && !hasAccessToken) {
        diagnosis.push('');
        diagnosis.push('⚠️ トークンが返されていません！');
        diagnosis.push('→ バックエンドがCognito認証を実行していない可能性');
        diagnosis.push('→ LineLinkService.linkLineAccount() を確認');
        diagnosis.push('→ CognitoAdminService.authenticateWithLine() を確認');
      }

      if (hasIdToken && hasAccessToken && hasRefreshToken) {
        diagnosis.push('');
        diagnosis.push('✅ 全トークンあり - 自動ログインが可能');
        diagnosis.push('→ FLARO Webの/auth/line-callbackにリダイレクトすべき');
      }
    } else {
      diagnosis.push('⚠️ APIレスポンスがデバッグ情報に含まれていません');
    }

    return (
      <div className="mb-4">
        <button
          onClick={() => setShowDebugPanel(!showDebugPanel)}
          className="w-full bg-gray-800 text-white py-2 px-4 rounded-lg text-sm font-mono flex items-center justify-between"
        >
          <span>🔧 Debug Panel</span>
          <span>{showDebugPanel ? '▲' : '▼'}</span>
        </button>
        {showDebugPanel && (
          <div className="mt-2 bg-gray-900 text-green-400 p-4 rounded-lg text-xs font-mono overflow-x-auto max-h-[80vh] overflow-y-auto">
            <div className="space-y-4">
              {/* 診断結果（最重要） */}
              <div>
                <div className="text-yellow-400 font-bold mb-2">🩺 診断結果:</div>
                <div className="space-y-1">
                  {diagnosis.map((item, index) => (
                    <div
                      key={index}
                      className={
                        item.startsWith('✅')
                          ? 'text-green-400'
                          : item.startsWith('❌')
                          ? 'text-red-400'
                          : item.startsWith('⚠️')
                          ? 'text-orange-400'
                          : item.startsWith('→')
                          ? 'text-gray-400 pl-4'
                          : 'text-gray-300'
                      }
                    >
                      {item}
                    </div>
                  ))}
                </div>
              </div>

              {/* FLARO URL */}
              <div>
                <div className="text-yellow-400 font-bold mb-2">🔗 FLARO URL (FLAROを開くボタン):</div>
                <div className="text-cyan-400 break-all">
                  {getFlaroUrl()}
                </div>
              </div>

              {/* デバッグログ */}
              {debugInfo?.debugLogs && debugInfo.debugLogs.length > 0 && (
                <div>
                  <div className="text-yellow-400 font-bold mb-2">📋 Debug Logs ({debugInfo.debugLogs.length}件):</div>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {debugInfo.debugLogs.map((log, index) => (
                      <div key={index} className="text-gray-300 text-[10px]">
                        {log}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* API レスポンス */}
              {apiResponse && (
                <div>
                  <div className="text-yellow-400 font-bold mb-2">📡 API Response (POST /v1/line/link):</div>
                  <pre className="whitespace-pre-wrap break-all text-gray-300 text-[10px]">
                    {JSON.stringify(apiResponse, null, 2)}
                  </pre>
                </div>
              )}

              {/* State情報 */}
              <div>
                <div className="text-yellow-400 font-bold mb-2">📦 Complete State:</div>
                <pre className="whitespace-pre-wrap break-all text-gray-300">
                  {JSON.stringify(
                    {
                      staffName: state.staffName,
                      lineDisplayName: state.lineDisplayName,
                      env: state.env,
                      companyCode: state.companyCode,
                    },
                    null,
                    2
                  )}
                </pre>
              </div>

              {/* デバッグ情報がない場合 */}
              {!debugInfo && (
                <div className="text-orange-400">
                  ⚠️ No debug info passed from LinkPage.
                  <br />
                  This may happen if navigating directly to this page.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-md mx-auto">
        {/* デバッグパネル */}
        {renderDebugPanel()}

        <Card className="text-center">
          <div className="space-y-6">
            {/* 成功アイコン */}
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <svg
                className="w-10 h-10 text-green-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>

            {/* メッセージ */}
            <div>
              <h1 className="text-2xl font-bold text-gray-900">連携完了</h1>
              <p className="text-gray-500 mt-2">
                LINE連携が正常に完了しました
              </p>
            </div>

            {/* 連携情報 */}
            <div className="bg-gray-50 rounded-lg p-4 text-left">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-500">スタッフ名</span>
                  <span className="font-medium text-gray-900">
                    {state.staffName}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">LINE表示名</span>
                  <span className="font-medium text-gray-900">
                    {state.lineDisplayName}
                  </span>
                </div>
              </div>
            </div>

            {/* 説明 */}
            <div className="text-sm text-gray-500 bg-blue-50 p-4 rounded-lg">
              <p>
                これでLINEアカウントでFLAROにログインできるようになりました。
                次回からはLINEでログインをお選びください。
              </p>
            </div>

            {/* ボタン */}
            <div className="space-y-3">
              <Button variant="primary" fullWidth onClick={handleOpenFlaro}>
                FLAROを開く
              </Button>
              <Button variant="secondary" fullWidth onClick={handleClose}>
                閉じる
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default CompletePage;
